/**
 * Resume message for an entity-disambiguation Continue.
 *
 * The card resumes with two different strings:
 * - `displayMessage` — what the user sees in the left panel: the entity list joined with " and ".
 * - `apiMessage` — what `llmcompile-invoke` receives. Identical to the display message unless the
 *   user ignored suggested names, in which case it also instructs the backend to disregard them.
 */

const IGNORE_PROMPT_TAIL = 'during disambiguation and proceed';

export interface ClarificationResumeMessages {
    /** Shown as the user's question in the conversation. */
    displayMessage: string;
    /** Sent as `message` on the resume call. */
    apiMessage: string;
}

/**
 * @param selectedNames entity names the user is proceeding with (already de-duplicated)
 * @param ignoredNames  entity names the user marked as Ignore on the card
 */
export function buildClarificationResumeMessages(
    selectedNames: string[],
    ignoredNames: string[] = [],
): ClarificationResumeMessages {
    const selected = selectedNames.map((n) => n.trim()).filter(Boolean);
    const ignored = ignoredNames.map((n) => n.trim()).filter(Boolean);

    // Synthetic "proceed" is the skip/no-selection signal the layout hides from the transcript.
    const displayMessage = selected.length > 0 ? selected.join(' and ') : 'proceed';

    if (ignored.length === 0) {
        return { displayMessage, apiMessage: displayMessage };
    }

    const plural = ignored.length > 1;
    const invalid = `${ignored.join(', ')} ${plural ? 'are not valid entity names' : 'is not a valid entity name'}`;
    const ignoreClause = `Ignore ${plural ? 'them' : 'it'} ${IGNORE_PROMPT_TAIL}`;
    const apiMessage =
        selected.length > 0
            ? `${invalid}. ${ignoreClause} with ${selected.join(', ')}`
            : `${invalid}. ${ignoreClause}`;

    return { displayMessage, apiMessage };
}

/**
 * Inverse of the above, for history: the backend persists `apiMessage`, so a reopened thread would
 * otherwise show the whole ignore instruction as the user's question. Recover the entity list that
 * was displayed live. Any other text is returned unchanged.
 */
export function normalizeClarificationResumeDisplay(message: string): string {
    if (typeof message !== 'string') return message;
    const match = new RegExp(
        `\\bIgnore (?:them|it) ${IGNORE_PROMPT_TAIL} with (.+)$`,
        'i',
    ).exec(message.trim());
    if (!match) return message;
    const names = match[1]
        .split(',')
        .map((n) => n.trim())
        .filter(Boolean);
    return names.length > 0 ? names.join(' and ') : message;
}
